import ListFilters from '@/common/components/ListFilters'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { formatCurrency } from '@/common/utils/formatters'
import { CreateSupplyAuthorization } from '@/features/billingBills/components/SupplyAuthorizationsTab/CreateSupplyAuthorization'
import {
  SupplyAuthorizationTotals,
  SupplyAuthorizationTotalsSkeleton,
} from '@/features/billingBills/components/SupplyAuthorizationsTab/SupplyAuthorizationTotals'
import { billSupplyAuthorizationTableColumns } from '@/features/billingBills/constants/billSupplyAuthorizationTableColumns.constant'
import { useBillingBillSupplyAuthorizations } from '@/features/billingBills/hooks/useBillingBillSupplyAuthorizations'
import { useBillingSupplyAuthorizationsEstimate } from '@/features/billingBills/hooks/useBillingSupplyAuthorizationsEstimate'
import { BillingBillSupplyAuthorizationTransformer } from '@/features/billingBills/services/billingBillSupplyAuthorizationTransformer'
import {
  BillingBillSupplyAuthorization,
  BillingBillSupplyAuthorizationTableItem,
} from '@/features/billingBills/types/billingBillSupplyAuthorization.type'
import { billingSupplyAuthorizationStatusOptions } from '@/features/supplyAuthorizations/constants/billingSupplyAuthorizationStatus.constant'
import { Search } from 'lucide-react'
import { useParams } from 'react-router-dom'

const SupplyAuthorizationsTab = () => {
  const { id: billId } = useParams()

  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce(filterParams.fiscal_document_code || '')

  const { data: billingSupplyAuthorizationEstimate, isPending: billingSupplyAuthorizationEstimateIsPending } =
    useBillingSupplyAuthorizationsEstimate({ billId })

  const {
    data: billSupplyAuthorizations,
    meta,
    isPending: billSupplyAuthorizationsIsPending,
  } = useBillingBillSupplyAuthorizations({
    filters: {
      id: billId,
      search: search,
      page: Number(filterParams.page) || 1,
      governmentAllocationId: filterParams.governmentAllocation?.value || '',
      status: filterParams.status?.value || '',
    },
    mapper: Mapper.mapWithTransformer<BillingBillSupplyAuthorization[], BillingBillSupplyAuthorizationTableItem[]>({
      transformer: BillingBillSupplyAuthorizationTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-2">
      {billingSupplyAuthorizationEstimateIsPending ? (
        <SupplyAuthorizationTotalsSkeleton />
      ) : (
        <SupplyAuthorizationTotals totals={billingSupplyAuthorizationEstimate?.totals} />
      )}

      <TextField
        LeftIcon={<Search />}
        placeholder="Pesquisar código da AFs"
        className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
        onChange={(e) => {
          setSearch((e.target as HTMLInputElement).value)
          setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
        }}
        value={filterParams.search}
      />

      <div className="flex justify-between items-center gap-2">
        <ListFilters
          filters={[
            {
              className: 'min-w-40',
              placeholder: 'Status',
              options: [{ label: 'Todos', value: '' }, ...billingSupplyAuthorizationStatusOptions],
              onChange: (e: any) =>
                setFilterParams((prev) => ({ ...prev, status: e.value ? { value: e.value, label: e.label } : null })),
              value: filterParams.status || '',
            },
            {
              className: 'min-w-40',
              placeholder: 'Empenho',
              options: [
                { label: 'Todos', value: '' },
                ...(billingSupplyAuthorizationEstimate?.government_allocations?.map((allocation) => ({
                  label: (
                    <>
                      #{allocation.office_number}{' '}
                      <span className="font-light italic text-nowrap">
                        ({formatCurrency(Number(allocation.consumed_total) - Number(allocation.total_of_supplies))}{' '}
                        Pendentes)
                      </span>
                    </>
                  ),
                  value: allocation.id,
                })) || []),
              ],
              disabled: billingSupplyAuthorizationEstimateIsPending,
              onChange: (e: any) =>
                setFilterParams((prev) => ({
                  ...prev,
                  governmentAllocation: e.value
                    ? {
                        value: e.value,
                        label: `#${
                          billingSupplyAuthorizationEstimate?.government_allocations?.find((a) => a.id === e.value)
                            ?.office_number || e.value
                        }`,
                      }
                    : null,
                })),
              value: filterParams.governmentAllocation || '',
            },
          ]}
        />

        <CreateSupplyAuthorization
          billId={billId}
          billingSupplyAuthorizationEstimate={billingSupplyAuthorizationEstimate}
          disabled={
            billingSupplyAuthorizationEstimateIsPending ||
            (billingSupplyAuthorizationEstimate &&
              Number(billingSupplyAuthorizationEstimate?.totals.consumed_total) -
                Number(billingSupplyAuthorizationEstimate?.totals.total_of_supplies) <=
                0)
          }
        />
      </div>

      <Table
        className="max-h-[calc(100vh-23.875rem)]"
        rows={billSupplyAuthorizations || []}
        columns={billSupplyAuthorizationTableColumns}
        isLoadingData={billSupplyAuthorizationsIsPending}
        page={meta.current_page}
        totalPages={meta.total_pages}
        totalCount={meta.total}
        hasPagination
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        rowEmptyValue="--"
      />
    </div>
  )
}

export default SupplyAuthorizationsTab
